/**
 * ChangePassword — forced first-login password change for tenants who
 * received a provisional password from the master.
 *
 * Skips current_password requirement when the JWT carries
 * must_change_password=true; otherwise requires it (self-service change).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff, Shield, Check } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import LangFlags from "../components/LangFlags";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  pt: {
    title: "Defina sua nova senha",
    sub: "Por segurança, é necessário substituir a senha provisória antes de continuar.",
    sub_self: "Mantenha sua conta segura — atualize sua senha aqui.",
    cur_label: "Senha atual", new_label: "Nova senha",
    new_ph: "Mínimo 8 caracteres", confirm_label: "Confirme a nova senha",
    submit: "Salvar nova senha",
    requirements: ["8+ caracteres", "Letras + números recomendados"],
    success: "Senha atualizada!",
    must_match: "As senhas não coincidem.", min_len: "Mínimo 8 caracteres.",
  },
  en: {
    title: "Set your new password",
    sub: "For security, replace your provisional password before continuing.",
    sub_self: "Keep your account secure — update your password here.",
    cur_label: "Current password", new_label: "New password",
    new_ph: "8+ characters", confirm_label: "Confirm new password",
    submit: "Save new password",
    requirements: ["8+ characters", "Letters + numbers recommended"],
    success: "Password updated!",
    must_match: "Passwords don't match.", min_len: "Minimum 8 characters.",
  },
  es: {
    title: "Define tu nueva contraseña",
    sub: "Por seguridad, debes reemplazar la contraseña provisional antes de continuar.",
    sub_self: "Mantén tu cuenta segura — actualiza tu contraseña aquí.",
    cur_label: "Contraseña actual", new_label: "Nueva contraseña",
    new_ph: "8+ caracteres", confirm_label: "Confirma la nueva contraseña",
    submit: "Guardar nueva contraseña",
    requirements: ["8+ caracteres", "Letras + números recomendados"],
    success: "¡Contraseña actualizada!",
    must_match: "Las contraseñas no coinciden.", min_len: "Mínimo 8 caracteres.",
  },
};

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const isForced = !!user?.must_change_password;

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error(c.min_len);
    if (newPw !== confirmPw) return toast.error(c.must_match);
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        ...(isForced ? {} : { current_password: currentPw }),
        new_password: newPw,
      });
      toast.success(c.success);
      await refresh();
      // Route based on role.
      if (user?.role === "super_admin" || user?.role === "admin") navigate("/master");
      else navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="change-password-page" className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #0A1233 0%, #1A0E36 100%)" }}>
      <LangFlags className="absolute top-5 right-5" />
      <div className="w-full max-w-md rounded-2xl border bg-white/5 backdrop-blur p-7 space-y-5"
        style={{ borderColor: "rgba(201,162,78,0.3)" }}>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#C9A24E] text-[#0F0820] flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-white font-extrabold text-xl">{c.title}</h1>
            <p className="text-[12px] text-white/55">{isForced ? c.sub : c.sub_self}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {!isForced && (
            <PwField label={c.cur_label} value={currentPw} onChange={setCurrentPw}
              show={show} testid="cp-current" required />
          )}
          <PwField label={c.new_label} placeholder={c.new_ph} value={newPw} onChange={setNewPw}
            show={show} testid="cp-new" required minLength={8} />
          <PwField label={c.confirm_label} value={confirmPw} onChange={setConfirmPw}
            show={show} testid="cp-confirm" required minLength={8} />

          <button type="button" onClick={() => setShow((s) => !s)}
            className="text-[11px] text-white/55 inline-flex items-center gap-1 hover:text-white">
            {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {show ? "Hide" : "Show"} passwords
          </button>

          <ul className="text-[11px] text-white/55 space-y-1 pl-4">
            {c.requirements.map((r) => (
              <li key={r} className="flex items-center gap-1">
                <Check className="w-2.5 h-2.5 text-emerald-400 shrink-0" /> {r}
              </li>
            ))}
          </ul>

          <button type="submit" disabled={busy}
            data-testid="cp-submit"
            className="w-full h-11 rounded-lg bg-[#C9A24E] text-[#0F0820] font-bold hover:bg-[#D9B25E] disabled:opacity-60 inline-flex items-center justify-center gap-2 text-[13px]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {c.submit}
          </button>
        </form>
      </div>
    </div>
  );
}

function PwField({ label, value, onChange, show, testid, placeholder, required, minLength }) {
  return (
    <label className="block">
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">{label}</div>
      <input
        type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required} minLength={minLength}
        autoComplete="new-password" data-testid={testid}
        className="w-full h-10 rounded-lg border bg-white/5 text-white placeholder:text-white/30 px-3 text-[13px] focus:outline-none focus:border-[#C9A24E]"
        style={{ borderColor: "rgba(201,162,78,0.2)" }} />
    </label>
  );
}
